import {
  Grid,
  TextField,
  Typography,
  FormControlLabel,
  FormGroup,
  Checkbox,
  Tooltip,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import React, { useState, useMemo, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import palette from '../../theme/palette';
import InfoIcon from '../../svg/InfoIcon';

const useStyles = makeStyles({
  characterContainer: {
    textAlign: 'end',
    color: palette.grey[500]
  }
});

const TestSetting = forwardRef(({ testObj, handleNext }, ref) => {
  const classes = useStyles();
  const [name, setName] = useState(testObj.testName);
  const [description, setDescription] = useState(testObj.description);
  const [correctMark, setCorrectMark] = useState(testObj.marking.correct);
  const [incorrectMark, setIncorrectMark] = useState(testObj.marking.incorrect);
  const [totalMark, setTotalMark] = useState(testObj.marking.total);
  const [testDuration, setDuration] = useState(testObj.duration);
  const [startDate, setStartDate] = useState(testObj.startDate);
  const [startTime, setStartTime] = useState(testObj.startTime);
  const [practiceTest, setPracticeTest] = useState(testObj.isPracticeTest);
  const [publishResult, setPublishResult] = useState(testObj.isPublishResult);
  const [randomize, setRandomize] = useState(testObj.isRandomize);

  const [nameErr, setNameErr] = useState(false);
  const [correctMarkErr, setCorrectMarkErr] = useState(false);
  const [totalMarkErr, setTotalMarkErr] = useState(false);
  const [durationErr, setDurationErr] = useState(false);

  // Creating time array starting from 12:00 am to 11:45 pm
  const timeSpan = useMemo(() => {
    const startOfDayDate = moment().startOf('day');
    return new Array(24).fill().reduce(
      (acc, _, index) => [
        ...acc,
        ...Array(1)
          .fill()
          .map(() => moment(startOfDayDate).add(index, 'hour')),
        ...Array(1)
          .fill()
          .map(() => moment(startOfDayDate).add(index, 'hour').add(15, 'minute')),
        ...Array(1)
          .fill()
          .map(() => moment(startOfDayDate).add(index, 'hour').add(30, 'minute')),
        ...Array(1)
          .fill()
          .map(() => moment(startOfDayDate).add(index, 'hour').add(45, 'minute'))
      ],
      []
    );
    // .map((d) => (d ? moment(d).format('h:mm a') : ''));
  }, []);

  const checkValidData = () => {
    const isNameErr = name.trim() !== '';
    let isCorrectMarkErr = true;
    let isTotalMarkErr = true;
    let isDurationErr = true;

    if (correctMark === 0) {
      isCorrectMarkErr = false;
      setCorrectMarkErr(true);
    } else if (correctMark === '') {
      isCorrectMarkErr = false;
      setCorrectMarkErr(true);
    } else {
      setCorrectMarkErr(false);
    }

    if (totalMark === 0) {
      isTotalMarkErr = false;
      setTotalMarkErr(true);
    } else if (totalMark === '') {
      isTotalMarkErr = false;
      setTotalMarkErr(true);
    } else {
      setTotalMarkErr(false);
    }

    if (testDuration === 0) {
      isDurationErr = false;
      setDurationErr(true);
    } else if (testDuration === '') {
      isDurationErr = false;
      setDurationErr(true);
    } else {
      setDurationErr(false);
    }

    setNameErr(name.trim() === '');
    return isNameErr && isCorrectMarkErr && isTotalMarkErr && isDurationErr;
  };

  useImperativeHandle(ref, () => ({
    handleSettingSubmit() {
      // Converting start time with selected date
      const dateFromStart = moment(startDate).format('yyyy-MM-DD');
      const timeFromStart = moment(startTime).format('HH:mm:ss');
      const newStartTime = moment(`${dateFromStart} ${timeFromStart}`);

      if (checkValidData()) {
        const obj = {
          testName: name,
          description,
          marking: {
            correct: correctMark,
            incorrect: incorrectMark,
            total: totalMark
          },
          duration: testDuration,
          startDate,
          startTime: newStartTime.toISOString(),
          isPracticeTest: practiceTest,
          isPublishResult: publishResult,
          isRandomize: randomize
        };
        handleNext(obj);
      }
    }
  }));

  return (
    <div style={{ marginTop: 15 }}>
      <TextField
        placeholder="Example: Class test, mid test..."
        label="Test Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        fullWidth
        margin="dense"
        error={nameErr}
        helperText={nameErr ? 'Please enter test name' : ''}
      />
      <TextField
        fullWidth
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        margin="dense"
        label="Description (optional)"
        placeholder="Write here...."
        multiline
        inputProps={{
          maxLength: 100
        }}
        minRows={5}
        maxRows={5}
      />
      <div className={classes.characterContainer}>{`Character ${description.length}/100`}</div>
      <div style={{ marginTop: 5 }}>
        <Typography>Marking Scheme</Typography>
        <Grid
          container
          spacing={2}
          sx={{
            marginTop: '1px'
          }}
        >
          <Grid item xs={12} md={4} sm={4}>
            <Typography
              sx={{
                textAlign: 'center',
                color: palette.grey[500],
                fontSize: 14
              }}
            >
              CORRECT
            </Typography>
            <TextField
              placeholder="0"
              value={correctMark}
              onChange={(e) => {
                setCorrectMark(e.target.value);
                if (e.target.value > 0) {
                  if (testObj.testType.toLowerCase() === 'type') {
                    setTotalMark(testObj.questions.length * e.target.value);
                  }
                  if (
                    testObj.testType.toLowerCase() === 'upload' &&
                    testObj?.questions.length > 0
                  ) {
                    setTotalMark(testObj.questions.length * e.target.value);
                  }
                } else {
                  setTotalMark(0);
                }
              }}
              fullWidth
              margin="dense"
              type="number"
              error={correctMarkErr}
              helperText={correctMarkErr ? 'Please enter mark' : ''}
            />
          </Grid>
          <Grid item xs={12} md={4} sm={4}>
            <Typography
              sx={{
                textAlign: 'center',
                color: palette.grey[500],
                fontSize: 14
              }}
            >
              INCORRECT
            </Typography>
            <TextField
              placeholder="0"
              value={incorrectMark}
              onChange={(e) => setIncorrectMark(e.target.value)}
              fullWidth
              margin="dense"
              type="number"
            />
          </Grid>
          <Grid item xs={12} md={4} sm={4}>
            <Typography
              sx={{
                textAlign: 'center',
                color: palette.grey[500],
                fontSize: 14
              }}
            >
              TOTAL MARKS
            </Typography>
            <TextField
              placeholder="0"
              disabled={
                // eslint-disable-next-line
                testObj.testType === 'type'
                  ? true
                  : testObj.testType === 'upload'
                  ? testObj?.options.length > 0
                  : false
              }
              value={totalMark}
              onChange={(e) => setTotalMark(e.target.value)}
              fullWidth
              margin="dense"
              type="number"
              error={totalMarkErr}
              helperText={totalMarkErr ? 'Please enter mark' : ''}
            />
          </Grid>
        </Grid>
      </div>
      <TextField
        placeholder="Enter time duration of test"
        margin="dense"
        label="Duration of Test (in minutes)"
        value={testDuration}
        onChange={(e) => setDuration(e.target.value)}
        fullWidth
        type="number"
        sx={{
          marginTop: 3
        }}
        error={durationErr}
        helperText={durationErr ? 'Please enter test duration' : ''}
      />
      <Grid
        container
        spacing={2}
        sx={{
          marginTop: '1px'
        }}
      >
        <Grid item xs={12} md={6} sm={6}>
          <LocalizationProvider fullWidth dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start date"
              value={startDate}
              minDate={new Date()}
              onChange={(newValue) => {
                setStartDate(newValue);
              }}
              renderInput={(params) => (
                <TextField placeholder="Select date" fullWidth margin="dense" {...params} />
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md={6} sm={6}>
          <FormControl sx={{ m: { xs: 0, md: 1 } }} fullWidth>
            <InputLabel id="demo-simple-select-label">Start time</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              label="Start time"
              value={moment(startTime).format('h:mm a')}
              onChange={(v) => {
                const date = timeSpan.find((t) => moment(t).format('h:mm a') === v.target.value);
                setStartTime(moment(date).toISOString());
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 48 * 4.5 + 8
                  }
                }
              }}
            >
              {timeSpan.map((t, index) => (
                <MenuItem key={index} value={moment(t).format('h:mm a')}>
                  {moment(t).format('h:mm a')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <FormGroup
        sx={{
          marginTop: 2
        }}
      >
        <div>
          <FormControlLabel
            control={
              <Checkbox
                checked={practiceTest}
                onChange={(e) => setPracticeTest(e.target.checked)}
              />
            }
            label="Make this a Practice Test."
          />
          <Tooltip title="Students can attempt this at any time." arrow>
            <IconButton>
              <InfoIcon color={palette.secondary.main} />
            </IconButton>
          </Tooltip>
        </div>
        <div>
          <FormControlLabel
            control={
              <Checkbox
                checked={publishResult}
                onChange={(e) => setPublishResult(e.target.checked)}
              />
            }
            label="Publish Results."
          />
          <Tooltip
            title="If selected, results will be visible to students automatically after the test."
            arrow
          >
            <IconButton>
              <InfoIcon color={palette.secondary.main} />
            </IconButton>
          </Tooltip>
        </div>
        {testObj.testType !== 'UPLOAD' && (
          <div>
            <FormControlLabel
              control={
                <Checkbox checked={randomize} onChange={(e) => setRandomize(e.target.checked)} />
              }
              label="Randomize questions for students."
            />
            <Tooltip
              title="If selected, question and option will  be randomized for students."
              arrow
            >
              <IconButton>
                <InfoIcon color={palette.secondary.main} />
              </IconButton>
            </Tooltip>
          </div>
        )}
      </FormGroup>
    </div>
  );
});

TestSetting.propTypes = {
  testObj: PropTypes.shape({
    testName: PropTypes.string,
    description: PropTypes.string,
    marking: PropTypes.shape({
      correct: PropTypes.number,
      incorrect: PropTypes.number,
      total: PropTypes.number
    }),
    duration: PropTypes.any,
    startDate: PropTypes.any,
    startTime: PropTypes.any,
    isPracticeTest: PropTypes.bool,
    isPublishResult: PropTypes.bool,
    isRandomize: PropTypes.bool,
    testType: PropTypes.string,
    questions: PropTypes.array,
    options: PropTypes.array
  }),
  handleNext: PropTypes.func,
  handleBack: PropTypes.func
};

export default TestSetting;
